/** @jsxImportSource react */
import { Alert, Box, Chip, Divider, Drawer, IconButton, Link, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import { automationsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import Loading from '../common/Loading'
import { actionLabel } from './automationModel'

const STEP_ICON = {
  OK: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  FAILED: <ErrorOutlineIcon fontSize="small" color="error" />,
  SKIPPED: <RemoveCircleOutlineIcon fontSize="small" color="disabled" />,
}
const EVENT = { CREATED: 'created', UPDATED: 'updated', DELETED: 'deleted' }

/** The latest runs of one rule: which record, who triggered it, and what each step did. */
const RunHistoryDrawer = ({ rule, onClose }) => {
  const tenantId = useTenantKey()
  const { data: runs, isLoading, error } = useQuery({
    queryKey: [tenantId, 'automations', rule?.id, 'runs'],
    queryFn: () => automationsAPI.runs(rule.id),
    enabled: !!rule,
    refetchInterval: 10_000,
  })
  return (
    <Drawer anchor="right" open={!!rule} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}>
      <Stack direction="row" alignItems="center" sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>{rule?.name}</Typography>
          <Typography variant="caption" color="text.secondary">Latest 50 runs. Records that didn’t match aren’t listed.</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </Stack>
      <Divider />
      <Box sx={{ overflowY: 'auto', p: 2.5 }}>
        {isLoading && <Loading />}
        {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
        {runs?.length === 0 && <Typography variant="body2" color="text.secondary">No runs yet. It runs when a matching change happens.</Typography>}
        <Stack spacing={2} divider={<Divider flexItem />}>
          {runs?.map((run) => (
            <Box key={run.id}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip label={run.status === 'SUCCESS' ? 'Succeeded' : 'Failed'} color={run.status === 'SUCCESS' ? 'success' : 'error'} variant="outlined" />
                <Typography variant="caption" color="text.secondary">{formatRelativeTime(run.createdOn)}</Typography>
              </Stack>
              <Typography variant="body2">
                {run.eventType !== 'DELETED'
                  ? <Link component={RouterLink} to={`/records/${rule.entityType}/${run.recordId}`} underline="hover">{run.recordTitle || 'Record'}</Link>
                  : run.recordTitle || 'Record'}
                {' '}{EVENT[run.eventType]} by {run.actorEmail || 'someone'}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {run.steps.map((step, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ pt: 0.25 }}>{STEP_ICON[step.status]}</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" color={step.status === 'SKIPPED' ? 'text.disabled' : 'text.primary'}>{actionLabel(step.type)}</Typography>
                      {step.detail && (
                        <Typography variant="caption" color={step.status === 'FAILED' ? 'error.main' : 'text.secondary'} sx={{ wordBreak: 'break-word' }}>{step.detail}</Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </Drawer>
  )
}

export default RunHistoryDrawer
